// Settings → Finance — Task 23
//
// Company-wide finance settings page.
// Currently hosts the GP Calculation Method selector (Task 23).
// Future: Cost Category Mapping (Task 24 — blocked on backend).

import { RotateCcw, TrendingUp, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFinanceSettings } from '@/hooks/use-finance-settings';
import {
  GP_METHOD_LABELS,
  GP_METHOD_DESCRIPTIONS,
  type GpCalculationMethod,
} from '@/lib/gp-calculation';

const GP_METHODS: GpCalculationMethod[] = ['simple', 'contribution', 'fully-loaded'];

export default function SettingsFinance() {

  const {
    settings,
    hasUnsavedChanges,
    isSaving,
    updateSetting,
    saveSettings,
    resetToDefaults,
  } = useFinanceSettings();

  const handleSave = async () => {
    await saveSettings();
    toast.success('Finance settings saved successfully.');
  };

  const handleReset = () => {
    resetToDefaults();
    toast.info('Finance settings reset to defaults. Save to apply.');
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Finance Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure company-wide finance calculation methods and reporting preferences.
          </p>
        </div>

        {/* ── GP Calculation Method ── */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">GP Calculation Method</CardTitle>
                <CardDescription className="mt-1">
                  Choose how Gross Profit and Gross Margin % are calculated across all Financials
                  tab cards, charts, and tables. This setting applies to every project company-wide.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">

            {/* Info banner */}
            <Alert variant="info">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Changing this setting immediately affects how GP figures are displayed across all
                project Financials tabs. Historical data is not recalculated — only the display
                method changes.
              </AlertDescription>
            </Alert>

            {/* Radio options */}
            <div className="space-y-3">
              {GP_METHODS.map((method) => {
                const isSelected = settings.gpCalculationMethod === method;
                return (
                  <div
                    key={method}
                    onClick={() => updateSetting('gpCalculationMethod', method)}
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    {/* Radio indicator */}
                    <div
                      className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-primary' : 'border-muted-foreground/40'
                      }`}
                    >
                      {isSelected && (
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>

                    {/* Label + description */}
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {GP_METHOD_LABELS[method]}
                        {method === 'simple' && (
                          <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {GP_METHOD_DESCRIPTIONS[method]}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="gap-1.5 text-muted-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset to defaults
              </Button>

              <div className="flex items-center gap-3">
                {hasUnsavedChanges && (
                  <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                )}
                <Button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  size="sm"
                >
                  {isSaving ? 'Saving…' : 'Save settings'}
                </Button>
              </div>
            </div>

          </CardContent>
        </Card>



      </div>
    </div>
  );
}
